/**
 * CosmeticPreview — Phase F1.
 *
 * Single visual-preview component used by the season pass tiles, the
 * lantern shop tiles, bundle content stacks, and the locker. Takes a
 * catalog id and renders the cosmetic on a neutral base avatar so
 * players can see what they're claiming/buying instead of a generic
 * placeholder thumbnail.
 *
 * For shape kinds (body / hat / face / accent) we parse the numeric
 * suffix off the catalog id and feed it through IconAvatar against a
 * neutral grey-blue base. Borders, backgrounds, and palettes are
 * rendered by setting the matching IconValue field. Particles + titles
 * don't have an IconAvatar representation — they get small inline
 * preview chips instead.
 *
 * Falls back to a small empty rarity-tinted box when the id is not in
 * the catalog (legacy shop ids, server/client drift).
 */
import type { CSSProperties } from 'react';
import { getCosmetic, type CosmeticDef } from '@tt/lobby/cosmetics-catalog';
import { IconAvatar, type IconValue } from './IconAvatar';

interface Props {
  cosmeticId: string;
  /** Edge length in px. Default 96. */
  size?: number;
  /** Reserved for callers that want the parchment-frame variant
   *  (currently unused — kept on the prop API for future use). */
  framed?: boolean;
}

const NEUTRAL_BASE: IconValue = {
  set: 'default',
  id: 0,
  hue: 220,           // neutral grey-blue body
  body: 0,            // basic blob silhouette
  hat: 0,
  face: 0,
  accentHue: 40,      // warm amber accent so accent-driven cosmetics read
};

/** Fixed-size, overflow-clipped wrapper. Phase F-fixes — every preview
 *  is wrapped in a div sized exactly to `size` with `overflow: hidden`
 *  + a circular clip so border layers that draw OUTSIDE the IconAvatar's
 *  100x100 viewBox (e.g. the brass-frame ring at r=47 with strokeWidth 6,
 *  ember-ring's outer flames) don't bleed past the tile padding into
 *  the neighbouring grid cell. The IconAvatar inside still gets the
 *  full `size` so its proportions are unchanged — only the rare
 *  out-of-viewBox stroke pixels get clipped. Background renders that
 *  fill the whole 100x100 viewBox already fit cleanly; the radius:50%
 *  matches the avatar circle so they don't show square corners. */
function PreviewBox({ size, children, circular = true }: { size: number; children: React.ReactNode; circular?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        overflow: 'hidden',
        borderRadius: circular ? '50%' : 12,
        flex: '0 0 auto',
      }}
    >
      {children}
    </span>
  );
}

export function CosmeticPreview({ cosmeticId, size = 96, framed: _framed = false }: Props) {
  void _framed;
  const def = getCosmetic(cosmeticId);
  if (!def) return <FallbackTile size={size} />;

  switch (def.kind) {
    case 'body': {
      const idx = parseSuffix(cosmeticId, /^body-(-?\d+)$/);
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, body: idx ?? 0 }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'hat': {
      const idx = parseSuffix(cosmeticId, /^hat-(-?\d+)$/);
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, hat: idx ?? 0 }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'face': {
      const idx = parseSuffix(cosmeticId, /^face-(-?\d+)$/);
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, face: idx ?? 0 }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'accent': {
      const hue = parseSuffix(cosmeticId, /^accent-(-?\d+)$/) ?? 0;
      // Pin a small hat (idx 1 = Wizard) so the accent fill is visible —
      // the accent ring otherwise reads as the hat's outer color.
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, hat: 1, accentHue: hue }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'palette': {
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, paletteId: cosmeticId }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'border': {
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, border: cosmeticId }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'background': {
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, background: cosmeticId }} size={size} alt={def.name} /></PreviewBox>;
    }
    case 'particles': {
      return <ParticlePreview def={def} size={size} />;
    }
    case 'title': {
      return <TitleChip text={def.name} size={size} />;
    }
    // Phase F5 — seat-tile cosmetics. Render a tiny seat-tile chip with
    // the matching CSS class applied so the preview looks like a
    // miniature lobby seat. Reusing the full `SeatPreview` component
    // would introduce extra chrome (lamp, name, status line) that
    // doesn't fit a tile-sized thumbnail; an inline div with the same
    // class names does the visual job in ~64px.
    case 'seat-border': {
      const id = parseStringSuffix(cosmeticId, /^seat-border-(.+)$/) ?? 'none';
      return <SeatChipPreview size={size} borderId={id} aria={def.name} />;
    }
    case 'seat-background': {
      const id = parseStringSuffix(cosmeticId, /^seat-background-(.+)$/) ?? 'plain';
      return <SeatChipPreview size={size} backgroundId={id} aria={def.name} />;
    }
    case 'seat-particle': {
      const id = parseStringSuffix(cosmeticId, /^seat-particle-(.+)$/) ?? 'none';
      return <SeatChipPreview size={size} particleId={id} aria={def.name} />;
    }
    case 'seat-bghue': {
      const hue = parseSuffix(cosmeticId, /^seat-bghue-(-?\d+)$/);
      return <SeatChipPreview size={size} bgHue={hue} aria={def.name} />;
    }
    // Phase F5 — masks render as a face overlay on the neutral base
    // avatar. Parse the semantic id off the catalog id and feed it
    // through IconAvatar's mask slot.
    case 'mask': {
      const id = parseStringSuffix(cosmeticId, /^mask-(.+)$/) ?? '';
      return <PreviewBox size={size}><IconAvatar icon={{ ...NEUTRAL_BASE, mask: id }} size={size} alt={def.name} /></PreviewBox>;
    }
  }
}

/** Pull a non-empty trailing string off a `${kind}-${id}` style id. */
function parseStringSuffix(id: string, re: RegExp): string | null {
  const m = re.exec(id);
  return m ? m[1] ?? null : null;
}

/** Small inline seat-tile preview for the locker. Mirrors how
 *  `SeatPreview` composes seat classes (border / background /
 *  particle / data-bg-hue) but without the lamp + name + status
 *  chrome — those don't fit at thumbnail sizes. */
function SeatChipPreview({
  size, borderId, backgroundId, particleId, bgHue, aria,
}: {
  size: number;
  borderId?: string;
  backgroundId?: string;
  particleId?: string;
  bgHue?: number | null;
  aria: string;
}) {
  const className = [
    'cosmetic-preview-seat',
    'seat',
    `seat-border-${borderId ?? 'none'}`,
    `seat-bg-${backgroundId ?? 'plain'}`,
    `seat-particle-${particleId ?? 'none'}`,
  ].join(' ');

  const hasBgHue = bgHue !== null && bgHue !== undefined && Number.isFinite(bgHue);
  const dataProps = hasBgHue ? { 'data-bg-hue': String(bgHue) } : {};
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: 10,
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
  };
  if (hasBgHue) {
    (style as Record<string, unknown>)['--seat-bg-h'] = String(bgHue);
  }
  return (
    <span className={className} style={style} {...dataProps} aria-label={aria} />
  );
}

/** Pull a numeric trailing index off a `${kind}-${idx}` style id. */
function parseSuffix(id: string, re: RegExp): number | null {
  const m = re.exec(id);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Small empty placeholder used when the catalog has no entry for the
 *  id (unknown / legacy / server drift). Matches the visual weight of a
 *  shop tile thumb so layout doesn't shift. */
function FallbackTile({ size }: { size: number }) {
  return (
    <span
      className="cosmetic-preview-fallback"
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.35)',
        border: '1px solid rgba(214,161,74,0.25)',
        color: 'rgba(248,237,214,0.4)',
        fontSize: Math.max(10, Math.floor(size * 0.22)),
      }}
      aria-hidden="true"
    >
      ?
    </span>
  );
}

/** Particle effects render in the seat-preview pipeline, not the
 *  IconAvatar SVG. Show a rarity-tinted chip with a representative
 *  glyph so the catalog tile still has something to read. */
function ParticlePreview({ def, size }: { def: CosmeticDef; size: number }) {
  const tags = def.tags ?? [];
  let glyph = '✨';
  if (tags.includes('embers') || tags.includes('fire')) glyph = '🔥';
  else if (tags.includes('snow') || tags.includes('ice')) glyph = '❄';
  else if (tags.includes('leaves') || tags.includes('forest')) glyph = '🍃';
  else if (tags.includes('petals') || tags.includes('flower')) glyph = '🌸';
  return (
    <span
      className={`cosmetic-preview-particle rarity-${def.rarity}`}
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, rgba(74,143,203,0.45), rgba(22,36,60,0.6))',
        border: '1px solid rgba(214,161,74,0.3)',
        fontSize: Math.max(18, Math.floor(size * 0.42)),
      }}
      aria-label={def.name}
    >
      {glyph}
    </span>
  );
}

/** Titles are flavor text, not avatar layers. Render a parchment chip
 *  with the title in a calligraphic-leaning font so players can see the
 *  string they'd equip. Sized to read at 64-96px tile widths. */
function TitleChip({ text, size }: { text: string; size: number }) {
  // Compress the text size when the title is long so it still fits a
  // 64px tile — pick a font size proportional to width but capped.
  const fontPx = Math.max(9, Math.min(13, Math.floor((size / Math.max(8, text.length)) * 1.1)));
  return (
    <span
      className="cosmetic-preview-title"
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 6,
        background: 'linear-gradient(160deg, #f1e3bf 0%, #d6b78a 100%)',
        border: '1px solid rgba(120, 88, 40, 0.55)',
        color: '#3a2a14',
        fontFamily: '"Cinzel", "Cormorant Garamond", Georgia, serif',
        fontStyle: 'italic',
        fontWeight: 600,
        fontSize: fontPx,
        lineHeight: 1.15,
        boxShadow: 'inset 0 0 12px rgba(120,80,30,0.25)',
      }}
      aria-label={text}
    >
      {text}
    </span>
  );
}
